import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowRight, FiCalendar, FiClock, FiDollarSign, FiRotateCcw, FiTarget, FiTrendingUp } from 'react-icons/fi';
import InputPesos from '~/components/Inputs/InputPesos';
import Tooltip from '~/components/Tooltip';
import { pesos } from '~/utils/dinero';
import {
  horaCorta,
  precioEstimado,
  ReferenciaCobro,
  referenciaDeCobro,
  ReservaOcupacion,
  reservasQueVienen,
  Resultado,
  SalaInforme,
  simularMeta,
  Tarifa,
  valorEstimadoReserva,
  ValorParaTodas,
} from './ocupacion';
import { CambioTarifa, cambiosReales, tarifaDeSala } from './tarifas';

/**
 * "¿Y si hubiéramos llegado al X %?": el simulador de meta.
 *
 * Parte de lo que pasó de verdad en el período (la ocupación y lo cobrado) y
 * deja subir la meta desde ahí —nunca por debajo: lo reservado, reservado
 * está— para ver cuántas horas más habrían hecho falta, cuánto habrían valido
 * según la tarifa de cada sala, y el total que se habría hecho. Sirve para
 * fijar metas de venta con un número delante: "con 4,5 puntos más habríamos
 * vendido tanto".
 *
 * La meta se mueve arriba (general, repartida entre las salas según lo libre
 * que estuvo cada una) o sala por sala (y entonces la general sale de la
 * suma). Las tarifas se pueden cambiar aquí mismo, para probar precios sin
 * guardarlos o para guardarlos en la configuración sin salir del informe.
 *
 * Con «Reservadas», las reservas que vienen ocupan horas pero suman $0 (se
 * cobran en el check-out). Se les puede poner un valor estimado, solo para la
 * simulación, y así ver lo que falta en dinero contando con ellas.
 */

const pct = (x: number | null, d = 1) => (x === null ? '—' : `${(x * 100).toFixed(d)}%`);
/** 40 → "40%", 37.5 → "37.5%" (con punto, como el resto del informe). */
const metaTexto = (m: number) => `${Number.isInteger(m) ? m : m.toFixed(1)}%`;
const puntosTexto = (p: number) => {
  const r = Math.round(p * 10) / 10;
  const n = Number.isInteger(r) ? String(r) : r.toFixed(1);
  return `${n} punto${r === 1 ? '' : 's'}`;
};
const horas = (min: number) => {
  const h = Math.round((min / 60) * 10) / 10;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
};

const METAS = [40, 50, 60, 75, 100];
/** Sube a la siguiente media unidad: 35,53 → 36 · 35,2 → 35,5. */
const aMedio = (x: number) => Math.ceil(x * 2) / 2;
/** Para escribir un porcentaje 0–1 en el campo de una sala, con un decimal. */
const aCampo = (x: number | null) => (x === null ? '0' : String(Math.round(x * 1000) / 10));

/** Lo que se teclea, con coma o con punto, a número; NaN si no es un número. */
const leer = (texto: string) => parseFloat(String(texto).replace(',', '.'));

type Props = {
  isOpen: boolean;
  onClose: () => void;
  resultado: Resultado;
  /** Cómo está filtrado el informe, para que quede claro sobre qué se simula. */
  descripcionFiltros: string;
  /** Guarda tarifas en la configuración de las sedes (lo mismo que Ajustes). */
  onGuardarTarifas: (cambios: CambioTarifa[]) => Promise<void>;
};

const SimuladorMeta = ({ isOpen, onClose, resultado, descripcionFiltros, onGuardarTarifas }: Props) => {
  const toast = useToast();
  const actualPct = (resultado.ocupacion ?? 0) * 100;
  // La barra no baja de lo que hubo: el piso es la ocupación real, redondeada
  // hacia arriba a la media unidad para que el paso sea limpio.
  const piso = Math.min(100, aMedio(actualPct));
  // Arranca en la decena siguiente: una meta alcanzable, no el 100 % de golpe.
  const sugerida = Math.min(100, Math.max(piso, Math.ceil((actualPct + 1) / 10) * 10));
  // Se guarda como texto para poder teclear "37,5" sin que el campo se coma la
  // coma a medio escribir. Lo escrito puede pasar por debajo del piso un
  // momento (al teclear "4" camino de "40"); lo que se simula, nunca.
  const [texto, setTexto] = useState(String(sugerida));
  // Metas por sala (0–1). null = manda la meta general y se reparte.
  const [metasSala, setMetasSala] = useState<Record<string, number> | null>(null);
  // Tarifas de prueba (sin guardar). null = las configuradas.
  const [tarifasPrueba, setTarifasPrueba] = useState<Record<string, Tarifa> | null>(null);
  const [tarifasAbiertas, setTarifasAbiertas] = useState(false);
  // Valor estimado de cada reserva que viene (por id), sin guardar. null = sin estimar.
  const [valoresVienen, setValoresVienen] = useState<Record<string, number> | null>(null);
  const [vienenAbiertas, setVienenAbiertas] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setTexto(String(sugerida));
      setMetasSala(null);
      setTarifasPrueba(null);
      setTarifasAbiertas(false);
      setValoresVienen(null);
      setVienenAbiertas(false);
    }
  }, [isOpen, sugerida]);

  const vienen = useMemo(() => reservasQueVienen(resultado, new Date()), [resultado]);
  const referencia = useMemo(() => referenciaDeCobro(resultado), [resultado]);
  const nombreSala = useMemo(
    () => Object.fromEntries(resultado.porSala.map((s) => [s.sala.id, s.sala.nombre])),
    [resultado]
  );
  // Lo puesto a las que vienen, sumado por sala: es lo que entra en la simulación.
  const estimadoPorSala = useMemo(() => {
    if (!valoresVienen) return null;
    const suma: Record<string, number> = {};
    vienen.forEach((r) => {
      const v = valoresVienen[r.id] ?? 0;
      if (v > 0) suma[r.locationId] = (suma[r.locationId] ?? 0) + v;
    });
    return suma;
  }, [valoresVienen, vienen]);
  const conValorVienen = valoresVienen ? vienen.filter((r) => (valoresVienen[r.id] ?? 0) > 0).length : 0;

  const escrita = leer(texto);
  const metaEfectiva = Math.min(100, Math.max(piso, Number.isFinite(escrita) ? escrita : piso));
  // En el piso se simula exactamente lo que hubo (el piso va redondeado a la
  // media unidad; si no, quedarían unas centésimas de "más" con cara de dato).
  const metaSimulada = metaEfectiva <= piso ? resultado.ocupacion ?? 0 : metaEfectiva / 100;

  const sim = useMemo(
    () =>
      simularMeta(resultado, metaSimulada, { metasSala, tarifas: tarifasPrueba, estimadoVienen: estimadoPorSala }),
    [resultado, metaSimulada, metasSala, tarifasPrueba, estimadoPorSala]
  );
  const porSala = sim.modo === 'porSala';
  const hayEstimado = sim.estimadoVienen > 0;
  // Lo que enseña la barra de arriba: la meta pedida, o la general que sale de las salas.
  const metaArriba = porSala ? Math.round(sim.meta * 1000) / 10 : metaEfectiva;
  const sinMargen = piso >= 100;
  const enElPiso = sim.deMasMin <= 0;
  const puntos = sim.puntosDeMas;
  const metasDisponibles = METAS.filter((m) => m > piso);

  /** Mover la meta general: vuelve al reparto entre salas. */
  const cambiarGeneral = (m: number | string) => {
    setTexto(String(m));
    if (metasSala) setMetasSala(null);
  };
  /** Mover la meta de UNA sala: desde ahí mandan las salas y la general se recalcula. */
  const cambiarSala = (id: string, valorPct: number) => {
    const fila = sim.porSala.find((s) => s.sala.id === id);
    if (!fila || fila.disponibleMin <= 0) return;
    const base =
      metasSala ?? Object.fromEntries(sim.porSala.map((s) => [s.sala.id, s.ocupacionConMeta ?? 0]));
    const actualSala = fila.ocupacionActual ?? 0;
    // Hasta el piso (la real redondeada a 0,5) se simula exactamente lo que hubo.
    const pisoSala = Math.min(100, aMedio(actualSala * 100));
    const v = Number.isFinite(valorPct) && valorPct > pisoSala ? Math.min(1, valorPct / 100) : actualSala;
    setMetasSala({ ...base, [id]: v });
  };
  const volverAlReparto = () => {
    setTexto(String(Math.max(piso, aMedio(sim.meta * 100))));
    setMetasSala(null);
  };

  const salasDelInforme = useMemo(() => resultado.porSala.map((s) => s.sala), [resultado]);
  const guardar = async (cambios: CambioTarifa[]) => {
    await onGuardarTarifas(cambios);
    setTarifasPrueba(null);
    setTarifasAbiertas(false);
    toast({
      title: cambios.length === 1 ? 'Tarifa guardada' : `${cambios.length} tarifas guardadas`,
      description: 'El informe y Ajustes ya la usan.',
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='6xl' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent data-testid='simulador-meta'>
        <ModalHeader pb={2}>
          <HStack spacing={2}>
            <Icon as={FiTarget} color='blue.500' />
            <Text fontSize='md'>¿Y si hubiéramos llegado a una meta de ocupación?</Text>
          </HStack>
          <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1}>
            {descripcionFiltros}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* ── la meta ── */}
          <Box bg='blue.50' borderRadius='lg' p={4} mb={4}>
            <Flex gap={6} align='center' wrap='wrap'>
              <Box flex='1' minW='280px'>
                <HStack justify='space-between' mb={2} align='baseline'>
                  <HStack spacing={2} align='baseline'>
                    <Text fontSize='sm' fontWeight='semibold' color='blue.900'>
                      Meta de ocupación general
                    </Text>
                    {porSala && (
                      <Badge colorScheme='purple' fontSize='0.6em' data-testid='modo-por-sala'>
                        sale de las salas
                      </Badge>
                    )}
                  </HStack>
                  <HStack spacing={2} fontSize='sm' color='gray.600' align='baseline'>
                    <Text>
                      estuvimos en <b data-testid='ocupacion-real'>{pct(sim.ocupacionActual)}</b>
                    </Text>
                    <Icon as={FiArrowRight} alignSelf='center' />
                    <Text color='blue.700' fontWeight='bold' fontSize='xl' data-testid='meta-valor'>
                      {metaTexto(metaArriba)}
                    </Text>
                  </HStack>
                </HStack>
                <Slider
                  aria-label='Meta de ocupación'
                  min={piso}
                  max={100}
                  step={0.5}
                  value={metaArriba}
                  onChange={cambiarGeneral}
                  isDisabled={sinMargen}
                  colorScheme={porSala ? 'purple' : 'blue'}
                  focusThumbOnChange={false}
                >
                  <SliderTrack h='8px' borderRadius='full' bg='blue.100'>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={5} borderWidth='2px' borderColor={porSala ? 'purple.500' : 'blue.500'} />
                </Slider>
                <HStack justify='space-between' fontSize='10px' color='gray.500' mt={1}>
                  <Text>{metaTexto(piso)} · lo que hubo</Text>
                  <Text>
                    {porSala
                      ? 'cada sala tiene su meta abajo; mover esta barra vuelve a repartir'
                      : 'la barra solo sube: lo reservado ya no se puede quitar'}
                  </Text>
                  <Text>100%</Text>
                </HStack>
              </Box>
              <HStack spacing={3} align='flex-end'>
                <Box>
                  <Text fontSize='xs' color='gray.600' mb={1}>
                    Escribe la meta
                  </Text>
                  <NumberInput
                    size='sm'
                    maxW='104px'
                    min={piso}
                    max={100}
                    step={0.5}
                    clampValueOnBlur
                    value={porSala ? String(metaArriba) : texto}
                    onChange={(valorTexto) => cambiarGeneral(valorTexto)}
                    isDisabled={sinMargen}
                    // Se puede teclear con coma o con punto: "37,5" y "37.5" valen.
                    isValidCharacter={(c) => /^[0-9.,]$/.test(c)}
                    parse={(v) => v.replace(',', '.')}
                    bg='white'
                    borderRadius='md'
                  >
                    <NumberInputField aria-label='Meta de ocupación en porcentaje' data-testid='meta-input' />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
                {metasDisponibles.length > 0 && (
                  <ButtonGroup size='sm' isAttached variant='outline'>
                    {metasDisponibles.map((m) => (
                      <Button
                        key={m}
                        bg={!porSala && metaEfectiva === m ? 'blue.500' : 'white'}
                        color={!porSala && metaEfectiva === m ? 'white' : 'gray.700'}
                        _hover={{ bg: !porSala && metaEfectiva === m ? 'blue.600' : 'gray.50' }}
                        onClick={() => cambiarGeneral(m)}
                      >
                        {m}%
                      </Button>
                    ))}
                  </ButtonGroup>
                )}
              </HStack>
            </Flex>
          </Box>

          {/* ── así estuvimos → así habría sido ── */}
          <Flex
            gap={3}
            mb={4}
            direction={{ base: 'column', md: 'row' }}
            align='stretch'
            data-testid='sim-comparacion'
          >
            <Box flex='1' borderWidth='1px' borderColor='gray.200' borderRadius='lg' px={4} py={3}>
              <Text fontSize='xs' color='gray.500' textTransform='uppercase' letterSpacing='wide' fontWeight='semibold'>
                Así estuvimos
              </Text>
              <Text fontSize='2xl' fontWeight='bold' lineHeight='1.2' mt={1} data-testid='sim-vendido'>
                {pesos.format(sim.vendido)}
              </Text>
              <Text fontSize='xs' color='gray.500' mt={0.5}>
                vendido de verdad · {pct(sim.ocupacionActual)} de ocupación · {horas(sim.ocupadoMin)} reservadas
              </Text>
              {vienen.length > 0 && (
                <Box mt={2} pt={2} borderTopWidth='1px' borderColor='gray.100' data-testid='vienen-resumen'>
                  {hayEstimado ? (
                    <>
                      <Text fontSize='md' fontWeight='bold' color='orange.600' data-testid='vienen-estimado'>
                        +{pesos.format(sim.estimadoVienen)} estimado
                      </Text>
                      <Text fontSize='xs' color='orange.800'>
                        de {conValorVienen} {conValorVienen === 1 ? 'reserva que viene' : 'reservas que vienen'}, sin
                        guardar
                      </Text>
                      <HStack spacing={3} mt={1}>
                        <Button
                          size='xs'
                          variant='link'
                          colorScheme='orange'
                          onClick={() => setVienenAbiertas(true)}
                          data-testid='cambiar-vienen'
                        >
                          Cambiar valores
                        </Button>
                        <Button
                          size='xs'
                          variant='link'
                          leftIcon={<FiRotateCcw />}
                          onClick={() => setValoresVienen(null)}
                          data-testid='quitar-vienen'
                        >
                          Quitar
                        </Button>
                      </HStack>
                    </>
                  ) : (
                    <>
                      <HStack spacing={1} fontSize='xs' color='gray.600'>
                        <Icon as={FiCalendar} />
                        <Text>
                          {vienen.length}{' '}
                          {vienen.length === 1 ? 'reserva que viene, aún sin valor' : 'reservas que vienen, aún sin valor'}
                        </Text>
                      </HStack>
                      <Button
                        size='xs'
                        mt={1.5}
                        variant='outline'
                        colorScheme='orange'
                        leftIcon={<FiDollarSign />}
                        onClick={() => setVienenAbiertas(true)}
                        data-testid='abrir-vienen'
                      >
                        Ponerles un valor
                      </Button>
                    </>
                  )}
                </Box>
              )}
              {vienen.length === 0 && resultado.diasFuturosFuera > 0 && (
                <Text fontSize='xs' color='gray.500' mt={2} pt={2} borderTopWidth='1px' borderColor='gray.100'>
                  Las reservas que vienen se ven con «Reservadas» en el informe.
                </Text>
              )}
            </Box>

            <Flex
              flex='1.3'
              direction='column'
              justify='center'
              align='center'
              textAlign='center'
              bg='green.50'
              borderWidth='1px'
              borderColor='green.200'
              borderRadius='lg'
              px={4}
              py={3}
            >
              {enElPiso ? (
                <Text fontSize='sm' color='gray.600'>
                  {sinMargen
                    ? 'El período ya estuvo al 100 %: no cabía ni una hora más.'
                    : 'Sube la meta para ver cuánto más habríamos vendido.'}
                </Text>
              ) : (
                <>
                  <Text fontSize='xs' color='green.800' textTransform='uppercase' letterSpacing='wide' fontWeight='semibold'>
                    Con {puntosTexto(puntos)} más
                  </Text>
                  <Text fontSize='3xl' fontWeight='extrabold' lineHeight='1.1' color='green.700' mt={1} data-testid='sim-venta'>
                    +{pesos.format(sim.ventaDeMas)}
                  </Text>
                  <HStack spacing={1} fontSize='xs' color='green.800' mt={1}>
                    <Icon as={FiClock} />
                    <Text data-testid='sim-horas'>{horas(sim.deMasMin)} más, a la tarifa de cada sala</Text>
                  </HStack>
                </>
              )}
            </Flex>

            <Box flex='1' borderWidth='1px' borderColor='blue.300' bg='blue.50' borderRadius='lg' px={4} py={3}>
              <Text fontSize='xs' color='blue.800' textTransform='uppercase' letterSpacing='wide' fontWeight='semibold'>
                Así habría sido
              </Text>
              <Text fontSize='2xl' fontWeight='bold' lineHeight='1.2' mt={1} color='blue.800' data-testid='sim-total'>
                {pesos.format(sim.totalConMeta)}
              </Text>
              <Text fontSize='xs' color='blue.700' mt={0.5}>
                al {metaTexto(metaArriba)} de ocupación · {horas(sim.ocupadoMin + sim.deMasMin)} reservadas
              </Text>
              {hayEstimado && (
                <Text fontSize='xs' color='orange.700' mt={0.5}>
                  incluye {pesos.format(sim.estimadoVienen)} estimado de las que vienen
                </Text>
              )}
            </Box>
          </Flex>

          {/* ── tarifas: configuradas o de prueba ── */}
          <Flex align='center' gap={3} mb={3} wrap='wrap'>
            <Button
              size='sm'
              variant={tarifasPrueba ? 'solid' : 'outline'}
              colorScheme={tarifasPrueba ? 'orange' : 'gray'}
              leftIcon={<FiDollarSign />}
              onClick={() => setTarifasAbiertas(true)}
              data-testid='abrir-tarifas'
            >
              {tarifasPrueba ? 'Precios de prueba' : 'Cambiar precios y descuentos'}
            </Button>
            {tarifasPrueba ? (
              <HStack spacing={2} fontSize='sm' color='orange.800' data-testid='aviso-tarifas-prueba'>
                <Text>Simulando con precios de prueba, sin guardar.</Text>
                <Button
                  size='xs'
                  variant='link'
                  colorScheme='orange'
                  leftIcon={<FiRotateCcw />}
                  onClick={() => setTarifasPrueba(null)}
                >
                  Volver a los configurados
                </Button>
              </HStack>
            ) : (
              <Text fontSize='sm' color='gray.500'>
                Para ver con qué precio o descuento te irías; también se pueden guardar desde aquí.
              </Text>
            )}
            <Box flex='1' />
            {porSala && (
              <Button
                size='sm'
                variant='outline'
                colorScheme='purple'
                leftIcon={<FiRotateCcw />}
                onClick={volverAlReparto}
                data-testid='volver-reparto'
              >
                Repartir según la meta general
              </Button>
            )}
          </Flex>

          {sim.salasSinTarifa > 0 && (
            <Text fontSize='xs' color='gray.500' mb={3}>
              {sim.salasSinTarifa === 1 ? 'Una sala no tiene' : `${sim.salasSinTarifa} salas no tienen`} precio por hora:
              sus horas de más sí se cuentan, pero su venta de más no se puede calcular; en «Así habría sido»
              aportan solo lo vendido{hayEstimado ? ' y lo estimado de las que vienen' : ''}. Ponles precio con el
              botón de arriba.
            </Text>
          )}

          {/* ── por sala ── */}
          <Table size='sm' variant='unstyled' data-testid='sim-tabla'>
            <Thead>
              <Tr borderBottomWidth='1px' borderColor='gray.200'>
                <Th pl={2} color='gray.500' fontSize='11px'>
                  Sala
                </Th>
                <Th color='gray.500' fontSize='11px' minW='280px'>
                  <Tooltip
                    hasArrow
                    fontSize='xs'
                    label='Ocupación que hubo → meta de la sala. Muévela aquí y la general de arriba se recalcula con la suma.'
                  >
                    <Text as='span' borderBottomWidth='1px' borderStyle='dotted' cursor='help'>
                      Ocupación: hubo → meta de la sala
                    </Text>
                  </Tooltip>
                </Th>
                <Th isNumeric color='gray.500' fontSize='11px'>
                  Horas de más
                </Th>
                <Th isNumeric color='gray.500' fontSize='11px'>
                  <Tooltip
                    hasArrow
                    fontSize='xs'
                    label='Precio por hora con el que se valoran las horas de más: el configurado, con su descuento (las horas libres se valoran como reservas de más de 1 hora).'
                  >
                    <Text as='span' borderBottomWidth='1px' borderStyle='dotted' cursor='help'>
                      Precio / hora
                    </Text>
                  </Tooltip>
                </Th>
                <Th isNumeric color='gray.500' fontSize='11px'>
                  Vendido
                </Th>
                {hayEstimado && (
                  <Th isNumeric color='orange.600' fontSize='11px'>
                    <Tooltip
                      hasArrow
                      fontSize='xs'
                      label='Valor que pusiste a las reservas que vienen de cada sala, que aún no se han cobrado. Es una estimación solo para esta simulación: no se guarda.'
                    >
                      <Text as='span' borderBottomWidth='1px' borderStyle='dotted' cursor='help'>
                        Vienen (estimado)
                      </Text>
                    </Tooltip>
                  </Th>
                )}
                <Th isNumeric color='gray.500' fontSize='11px'>
                  Venta de más
                </Th>
                <Th isNumeric color='gray.500' fontSize='11px' pr={2}>
                  Así habría sido
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {sim.porSala.map((s) => {
                const pisoSala = s.ocupacionActual === null ? 0 : Math.min(100, aMedio(s.ocupacionActual * 100));
                const metaSalaPct = (s.ocupacionConMeta ?? 0) * 100;
                const llena = s.disponibleMin > 0 && s.ocupadoMin >= s.disponibleMin;
                return (
                  <Tr key={s.sala.id} borderBottomWidth='1px' borderColor='gray.100' data-testid='sim-fila'>
                    <Td pl={2} py={2.5}>
                      <HStack spacing={2}>
                        <Text fontWeight='medium'>{s.sala.nombre}</Text>
                        {llena && (
                          <Badge colorScheme='green' fontSize='0.6em'>
                            llena
                          </Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td py={2.5}>
                      <HStack spacing={2}>
                        <Text color='gray.600' minW='44px' fontSize='sm'>
                          {pct(s.ocupacionActual)}
                        </Text>
                        <Icon as={FiArrowRight} color='gray.400' boxSize={3} />
                        <Slider
                          aria-label={`Meta de ${s.sala.nombre}`}
                          min={pisoSala}
                          max={100}
                          step={0.5}
                          value={Math.max(pisoSala, metaSalaPct)}
                          onChange={(v) => cambiarSala(s.sala.id, v)}
                          isDisabled={s.disponibleMin <= 0 || llena}
                          colorScheme={porSala ? 'purple' : 'blue'}
                          focusThumbOnChange={false}
                          w='110px'
                          size='sm'
                        >
                          <SliderTrack bg='gray.200'>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb boxSize={3.5} />
                        </Slider>
                        <CampoMetaSala
                          nombre={s.sala.nombre}
                          valor={aCampo(s.ocupacionConMeta)}
                          piso={pisoSala}
                          deshabilitado={s.disponibleMin <= 0 || llena}
                          onCambiar={(n) => cambiarSala(s.sala.id, n)}
                        />
                        <Text fontSize='xs' color='gray.500'>
                          %
                        </Text>
                      </HStack>
                      <Text fontSize='xs' color='gray.500' mt={0.5}>
                        {horas(s.ocupadoMin)} de {horas(s.disponibleMin)}
                      </Text>
                    </Td>
                    <Td isNumeric py={2.5}>
                      <HStack justify='flex-end' spacing={1}>
                        <Icon as={FiClock} color='gray.400' boxSize={3} />
                        <Text fontWeight='medium'>{s.deMasMin > 0 ? `+${horas(s.deMasMin)}` : '0 h'}</Text>
                      </HStack>
                    </Td>
                    <Td isNumeric py={2.5} color='gray.600'>
                      {s.precioEstimado === null ? (
                        <Badge colorScheme='gray' fontSize='0.6em'>
                          sin tarifa
                        </Badge>
                      ) : (
                        <>
                          <Text>{pesos.format(s.precioEstimado)}</Text>
                          {s.tarifa.descuento > 0 && (
                            <Text fontSize='xs' color='gray.500'>
                              {pesos.format(s.tarifa.precioHora ?? 0)} −{Math.round(s.tarifa.descuento * 100)}%
                            </Text>
                          )}
                        </>
                      )}
                    </Td>
                    <Td isNumeric py={2.5}>
                      {s.vendido ? <Text>{pesos.format(s.vendido)}</Text> : <Text color='gray.400'>—</Text>}
                    </Td>
                    {hayEstimado && (
                      <Td isNumeric py={2.5} data-testid='sim-vienen-sala'>
                        {s.estimadoVienen ? (
                          <Text color='orange.600'>+{pesos.format(s.estimadoVienen)}</Text>
                        ) : (
                          <Text color='gray.400'>—</Text>
                        )}
                      </Td>
                    )}
                    <Td isNumeric py={2.5}>
                      {s.ventaDeMas === null ? (
                        <Text color='gray.400'>—</Text>
                      ) : (
                        <Text color='green.600' fontWeight='medium'>
                          {s.ventaDeMas > 0 ? `+${pesos.format(s.ventaDeMas)}` : pesos.format(0)}
                        </Text>
                      )}
                    </Td>
                    <Td isNumeric py={2.5} pr={2}>
                      {s.totalConMeta === null ? (
                        // Sin tarifa no se sabe cuánto valdrían sus horas de más:
                        // se deja lo vendido (y lo estimado de las que vienen,
                        // si se puso), para que la columna siga sumando el total.
                        <Tooltip
                          hasArrow
                          fontSize='xs'
                          label={
                            s.estimadoVienen
                              ? 'Sin tarifa: lo vendido y lo estimado de las que vienen, sin la venta de más.'
                              : 'Sin tarifa: solo lo vendido, sin la venta de más.'
                          }
                        >
                          <Text color='gray.500' cursor='help'>
                            {s.vendido + s.estimadoVienen ? pesos.format(s.vendido + s.estimadoVienen) : '—'}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text fontWeight='semibold' color='blue.800'>
                          {pesos.format(s.totalConMeta)}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
              <Tr bg='gray.50' fontWeight='semibold'>
                <Td pl={2} py={3}>
                  Total
                </Td>
                <Td py={3}>
                  <HStack spacing={1}>
                    <Text>{pct(sim.ocupacionActual)}</Text>
                    {!enElPiso && (
                      <>
                        <Icon as={FiArrowRight} color='gray.400' boxSize={3} />
                        <Text color='blue.700'>{metaTexto(metaArriba)}</Text>
                      </>
                    )}
                  </HStack>
                </Td>
                <Td isNumeric py={3}>
                  {sim.deMasMin > 0 ? `+${horas(sim.deMasMin)}` : '0 h'}
                </Td>
                <Td isNumeric py={3} color='gray.500' fontWeight='normal'>
                  —
                </Td>
                <Td isNumeric py={3}>
                  {sim.vendido ? pesos.format(sim.vendido) : '—'}
                </Td>
                {hayEstimado && (
                  <Td isNumeric py={3} color='orange.600' data-testid='sim-vienen-total'>
                    +{pesos.format(sim.estimadoVienen)}
                  </Td>
                )}
                <Td isNumeric py={3} color='green.700'>
                  +{pesos.format(sim.ventaDeMas)}
                </Td>
                <Td isNumeric py={3} pr={2} color='blue.800'>
                  {pesos.format(sim.totalConMeta)}
                </Td>
              </Tr>
            </Tbody>
          </Table>

          <HStack spacing={1} mt={4} fontSize='xs' color='gray.500' align='flex-start'>
            <Icon as={FiTrendingUp} mt='2px' />
            <Text>
              «Vendido» es lo cobrado de verdad en el período. Con la meta general, las horas de más se reparten
              entre las salas en proporción a las horas libres de cada una; con metas por sala, la general sale de
              la suma. Las horas de más valen el <b>precio por hora con su descuento</b> (las horas libres se
              valoran como reservas de más de 1 hora). Al 100 % la venta de más coincide con «No vendido
              (tarifa)» del informe.
              {vienen.length > 0 &&
                ' Las reservas que vienen suman $0 hasta su check-out; el valor que les pongas es una estimación solo para esta simulación y no se guarda.'}
            </Text>
          </HStack>
        </ModalBody>

        {/* Dentro del contenido, no al lado: si no, la rueda no desplaza (ver modales-anidadas). */}
        <TarifasDeSalas
          isOpen={tarifasAbiertas}
          onClose={() => setTarifasAbiertas(false)}
          salas={salasDelInforme}
          iniciales={tarifasPrueba}
          onProbar={(t) => {
            setTarifasPrueba(t);
            setTarifasAbiertas(false);
          }}
          onGuardar={guardar}
        />
        <ReservasQueVienen
          isOpen={vienenAbiertas}
          onClose={() => setVienenAbiertas(false)}
          reservas={vienen}
          nombreSala={nombreSala}
          iniciales={valoresVienen}
          referencia={referencia}
          onUsar={(valores) => {
            setValoresVienen(Object.values(valores).some((v) => v > 0) ? valores : null);
            setVienenAbiertas(false);
          }}
        />
      </ModalContent>
    </Modal>
  );
};

/* ───────────────────────────  campo de meta de una sala  ─────────────────────────── */

/**
 * El número de la meta de una sala. Mientras se escribe manda lo tecleado
 * (si no, al teclear "6" camino de "60" el campo se corregiría al piso y el
 * "0" caería detrás); al salir, enseña la meta que quedó de verdad.
 */
const CampoMetaSala = ({
  nombre,
  valor,
  piso,
  deshabilitado,
  onCambiar,
}: {
  nombre: string;
  valor: string;
  piso: number;
  deshabilitado: boolean;
  onCambiar: (n: number) => void;
}) => {
  const [texto, setTexto] = useState(valor);
  const [conFoco, setConFoco] = useState(false);
  useEffect(() => {
    if (!conFoco) setTexto(valor);
  }, [valor, conFoco]);
  return (
    <NumberInput
      size='xs'
      maxW='72px'
      min={piso}
      max={100}
      step={0.5}
      value={texto}
      onChange={(v) => {
        setTexto(v);
        onCambiar(leer(v));
      }}
      onFocus={() => setConFoco(true)}
      onBlur={() => setConFoco(false)}
      isDisabled={deshabilitado}
      isValidCharacter={(c) => /^[0-9.,]$/.test(c)}
      parse={(v) => v.replace(',', '.')}
    >
      <NumberInputField
        aria-label={`Meta de ${nombre} en porcentaje`}
        data-testid='meta-sala-input'
        px={1.5}
        fontWeight='semibold'
        color='blue.700'
      />
      <NumberInputStepper>
        <NumberIncrementStepper />
        <NumberDecrementStepper />
      </NumberInputStepper>
    </NumberInput>
  );
};

/* ───────────────────────────  tarifas de las salas  ─────────────────────────── */

type TarifasProps = {
  isOpen: boolean;
  onClose: () => void;
  salas: SalaInforme[];
  /** Tarifas de prueba ya aplicadas, si las hay. */
  iniciales: Record<string, Tarifa> | null;
  onProbar: (tarifas: Record<string, Tarifa>) => void;
  onGuardar: (cambios: CambioTarifa[]) => Promise<void>;
};

type Borrador = Record<string, { precio: string; descuento: string }>;

const borradorDe = (salas: SalaInforme[], iniciales: Record<string, Tarifa> | null): Borrador =>
  Object.fromEntries(
    salas.map((s) => {
      const t = iniciales?.[s.id] ?? tarifaDeSala(s);
      return [s.id, { precio: String(t.precioHora ?? 0), descuento: String(Math.round(t.descuento * 100)) }];
    })
  );

const aTarifa = (b: { precio: string; descuento: string }): Tarifa => {
  const precio = Math.max(0, Math.round(leer(b.precio) || 0));
  const descuento = Math.min(100, Math.max(0, leer(b.descuento) || 0));
  return { precioHora: precio > 0 ? precio : null, descuento: descuento / 100 };
};

const TarifasDeSalas = ({ isOpen, onClose, salas, iniciales, onProbar, onGuardar }: TarifasProps) => {
  const [borrador, setBorrador] = useState<Borrador>({});
  const [todasPrecio, setTodasPrecio] = useState('');
  const [todasDescuento, setTodasDescuento] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setBorrador(borradorDe(salas, iniciales));
      setTodasPrecio('');
      setTodasDescuento('');
      setError(null);
    }
  }, [isOpen, salas, iniciales]);

  const tarifas = useMemo(
    () => Object.fromEntries(Object.entries(borrador).map(([id, b]) => [id, aTarifa(b)])) as Record<string, Tarifa>,
    [borrador]
  );
  const cambios: CambioTarifa[] = salas.map((s) => ({
    id: s.id,
    precioHora: tarifas[s.id]?.precioHora ?? 0,
    descuento: tarifas[s.id]?.descuento ?? 0,
  }));
  const distintosDeGuardado = cambiosReales(salas, cambios);

  const editar = (id: string, campo: 'precio' | 'descuento', valor: string) =>
    setBorrador((b) => ({ ...b, [id]: { ...b[id], [campo]: valor } }));
  const aplicarATodas = () => {
    setBorrador((b) =>
      Object.fromEntries(
        Object.entries(b).map(([id, v]) => [
          id,
          {
            precio: todasPrecio.trim() === '' ? v.precio : todasPrecio,
            descuento: todasDescuento.trim() === '' ? v.descuento : todasDescuento,
          },
        ])
      )
    );
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(distintosDeGuardado);
    } catch (e) {
      setError('No se pudo guardar. Revisa la conexión y vuelve a intentar.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='3xl' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent data-testid='tarifas-salas'>
        <ModalHeader pb={1}>
          <HStack spacing={2}>
            <Icon as={FiDollarSign} color='orange.500' />
            <Text fontSize='md'>Precios y descuentos de las salas</Text>
          </HStack>
          <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1}>
            Prueba aquí con qué precio o descuento te irías; si te convence, guárdalo y queda igual que en
            Ajustes → la sede → Bloques → Tarifa.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex
            gap={3}
            align='flex-end'
            wrap='wrap'
            bg='gray.50'
            borderRadius='md'
            px={3}
            py={2}
            mb={3}
            data-testid='tarifas-todas'
          >
            <Text fontSize='sm' fontWeight='medium' color='gray.700' pb={1.5}>
              Poner a todas:
            </Text>
            <Box>
              <Text fontSize='xs' color='gray.500' mb={0.5}>
                Precio por hora
              </Text>
              <InputPesos
                size='sm'
                maxW='140px'
                value={todasPrecio}
                onChange={(cifras) => setTodasPrecio(cifras)}
                bg='white'
                placeholder='p. ej. 90.000'
                aria-label='Precio por hora para todas, en pesos'
                data-testid='tarifas-todas-precio'
              />
            </Box>
            <Box>
              <Text fontSize='xs' color='gray.500' mb={0.5}>
                Descuento &gt; 1 h (%)
              </Text>
              <NumberInput size='sm' maxW='100px' min={0} max={100} value={todasDescuento} onChange={setTodasDescuento} bg='white'>
                <NumberInputField placeholder='p. ej. 30' aria-label='Descuento para todas' />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Box>
            <Button
              size='sm'
              variant='outline'
              onClick={aplicarATodas}
              isDisabled={todasPrecio.trim() === '' && todasDescuento.trim() === ''}
            >
              Aplicar a todas
            </Button>
          </Flex>

          <Table size='sm' variant='unstyled'>
            <Thead>
              <Tr borderBottomWidth='1px' borderColor='gray.200'>
                <Th pl={2} color='gray.500' fontSize='11px'>
                  Sala
                </Th>
                <Th color='gray.500' fontSize='11px'>
                  Precio por hora (COP)
                </Th>
                <Th color='gray.500' fontSize='11px'>
                  Descuento si dura más de 1 h (%)
                </Th>
                <Th isNumeric color='gray.500' fontSize='11px' pr={2}>
                  <Tooltip hasArrow fontSize='xs' label='Precio por hora con el descuento: con este se estima lo no vendido y la venta de más.'>
                    <Text as='span' borderBottomWidth='1px' borderStyle='dotted' cursor='help'>
                      Se estima a
                    </Text>
                  </Tooltip>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {salas.map((s) => {
                const b = borrador[s.id] ?? { precio: '0', descuento: '0' };
                const t = tarifas[s.id];
                const estimado = t ? precioEstimado(t) : null;
                const cambiado = distintosDeGuardado.some((c) => c.id === s.id);
                return (
                  <Tr key={s.id} borderBottomWidth='1px' borderColor='gray.100' data-testid='tarifa-fila'>
                    <Td pl={2} py={2}>
                      <HStack spacing={2}>
                        <Text fontWeight='medium'>{s.nombre}</Text>
                        {cambiado && (
                          <Badge colorScheme='orange' fontSize='0.6em'>
                            cambiado
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize='xs' color='gray.500'>
                        guardado: {s.precioHora ? pesos.format(s.precioHora) : 'sin precio'}
                        {s.descuento > 0 ? ` · −${Math.round(s.descuento * 100)}%` : ''}
                      </Text>
                    </Td>
                    <Td py={2}>
                      <InputPesos
                        size='sm'
                        maxW='150px'
                        value={b.precio}
                        onChange={(cifras) => editar(s.id, 'precio', cifras)}
                        aria-label={`Precio por hora de ${s.nombre}, en pesos`}
                        data-testid='tarifa-precio'
                      />
                    </Td>
                    <Td py={2}>
                      <NumberInput
                        size='sm'
                        maxW='100px'
                        min={0}
                        max={100}
                        value={b.descuento}
                        onChange={(v) => editar(s.id, 'descuento', v)}
                      >
                        <NumberInputField aria-label={`Descuento de ${s.nombre}`} data-testid='tarifa-descuento' />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Td>
                    <Td isNumeric py={2} pr={2} data-testid='tarifa-estimado'>
                      {estimado === null ? (
                        <Badge colorScheme='gray' fontSize='0.6em'>
                          sin tarifa
                        </Badge>
                      ) : (
                        <Text fontWeight='semibold'>{pesos.format(estimado)}</Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          {error && (
            <Alert status='error' borderRadius='md' mt={3} fontSize='sm'>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter gap={2} flexWrap='wrap'>
          <Text fontSize='xs' color='gray.500' flex='1' minW='200px'>
            «Probar» solo cambia la simulación; «Guardar» escribe la tarifa en la configuración de cada sala
            {distintosDeGuardado.length
              ? ` (${distintosDeGuardado.length} ${distintosDeGuardado.length === 1 ? 'sala cambia' : 'salas cambian'})`
              : ' (nada cambia todavía)'}
            .
          </Text>
          <Button variant='ghost' size='sm' onClick={onClose} isDisabled={guardando}>
            Cancelar
          </Button>
          <Button
            size='sm'
            variant='outline'
            colorScheme='orange'
            onClick={() => onProbar(tarifas)}
            isDisabled={guardando}
            data-testid='probar-tarifas'
          >
            Probar sin guardar
          </Button>
          <Button
            size='sm'
            colorScheme='blue'
            onClick={guardar}
            isLoading={guardando}
            loadingText='Guardando…'
            isDisabled={distintosDeGuardado.length === 0}
            data-testid='guardar-tarifas'
          >
            Guardar en la configuración
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/* ───────────────────────────  reservas que vienen  ─────────────────────────── */

/** Los mismos nombres y colores que el calendario (`COLORES` en CalendarioReservas). */
const ESTADOS: Record<string, { nombre: string; color: string }> = {
  pending: { nombre: 'Sin confirmar', color: 'green' },
  confirmed: { nombre: 'Confirmada', color: 'blue' },
  checkin: { nombre: 'En la sala', color: 'orange' },
};

type VienenProps = {
  isOpen: boolean;
  onClose: () => void;
  reservas: ReservaOcupacion[];
  nombreSala: Record<string, string>;
  /** Valores ya puestos en la simulación (por id de reserva), si los hay. */
  iniciales: Record<string, number> | null;
  referencia: ReferenciaCobro;
  onUsar: (valores: Record<string, number>) => void;
};

/**
 * Las reservas que vienen, con un valor estimado para cada una. Se puede
 * poner uno a todas (por hora, con descuento si dura más de 1 h, o por
 * reserva) y retocar cualquiera. Nada de esto se guarda: solo alimenta la
 * simulación.
 */
const ReservasQueVienen = ({ isOpen, onClose, reservas, nombreSala, iniciales, referencia, onUsar }: VienenProps) => {
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  // Por hora por defecto: duran de 1 a 12 horas, y un mismo valor por reserva
  // pondría igual una de 1 h que una de 10 h.
  const [modo, setModo] = useState<ValorParaTodas['modo']>('hora');
  const [todasValor, setTodasValor] = useState('');
  const [todasDescuento, setTodasDescuento] = useState('');
  useEffect(() => {
    if (isOpen) {
      setBorrador(
        Object.fromEntries(reservas.map((r) => [r.id, iniciales?.[r.id] ? String(iniciales[r.id]) : '']))
      );
      setModo('hora');
      setTodasValor('');
      setTodasDescuento('');
    }
  }, [isOpen, reservas, iniciales]);

  const valores = useMemo(
    () =>
      Object.fromEntries(reservas.map((r) => [r.id, Math.max(0, Math.round(leer(borrador[r.id] ?? '') || 0))])),
    [reservas, borrador]
  );
  const total = reservas.reduce((a, r) => a + (valores[r.id] ?? 0), 0);
  const conValor = reservas.filter((r) => (valores[r.id] ?? 0) > 0).length;
  const minutosTotales = reservas.reduce((a, r) => a + (r.fin - r.inicio), 0);

  const aplicarATodas = () => {
    const v: ValorParaTodas = {
      modo,
      valor: leer(todasValor) || 0,
      descuento: (leer(todasDescuento) || 0) / 100,
    };
    setBorrador(Object.fromEntries(reservas.map((r) => [r.id, String(valorEstimadoReserva(r, v))])));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='4xl' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent data-testid='reservas-vienen'>
        <ModalHeader pb={1}>
          <HStack spacing={2}>
            <Icon as={FiCalendar} color='orange.500' />
            <Text fontSize='md'>Reservas que vienen, aún sin cobrar</Text>
          </HStack>
          <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1}>
            Ya ocupan sus horas, pero el valor se anota en el check-out y hoy suman $0. Ponles lo que esperas
            cobrar para simular con ese dinero.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box bg='gray.50' borderRadius='md' px={3} py={2} mb={3} data-testid='vienen-todas'>
            <Flex gap={3} align='flex-end' wrap='wrap'>
              <Text fontSize='sm' fontWeight='medium' color='gray.700' pb={1.5}>
                Poner a todas:
              </Text>
              <Box>
                <Text fontSize='xs' color='gray.500' mb={0.5}>
                  Valor
                </Text>
                <InputPesos
                  size='sm'
                  maxW='150px'
                  value={todasValor}
                  onChange={(cifras) => setTodasValor(cifras)}
                  bg='white'
                  placeholder={modo === 'hora' ? 'p. ej. 90.000' : 'p. ej. 350.000'}
                  aria-label='Valor para todas, en pesos'
                  data-testid='vienen-todas-valor'
                />
              </Box>
              <ButtonGroup size='sm' isAttached variant='outline' pb='1px'>
                {(['hora', 'reserva'] as const).map((m) => (
                  <Button
                    key={m}
                    bg={modo === m ? 'blue.500' : 'white'}
                    color={modo === m ? 'white' : 'gray.700'}
                    _hover={{ bg: modo === m ? 'blue.600' : 'gray.50' }}
                    onClick={() => setModo(m)}
                    data-testid={`vienen-modo-${m}`}
                  >
                    {m === 'hora' ? 'por hora' : 'por reserva'}
                  </Button>
                ))}
              </ButtonGroup>
              <Box>
                <Text fontSize='xs' color={modo === 'hora' ? 'gray.500' : 'gray.300'} mb={0.5}>
                  Descuento &gt; 1 h (%)
                </Text>
                <NumberInput
                  size='sm'
                  maxW='100px'
                  min={0}
                  max={100}
                  value={todasDescuento}
                  onChange={setTodasDescuento}
                  isDisabled={modo !== 'hora'}
                  bg='white'
                >
                  <NumberInputField
                    placeholder='p. ej. 30'
                    aria-label='Descuento para todas'
                    data-testid='vienen-todas-descuento'
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
              <Button
                size='sm'
                variant='outline'
                onClick={aplicarATodas}
                isDisabled={todasValor.trim() === ''}
                data-testid='vienen-aplicar'
              >
                Aplicar a todas
              </Button>
            </Flex>
            {referencia.porHora !== null && referencia.porReserva !== null && (
              <Text fontSize='xs' color='gray.600' mt={2} data-testid='vienen-referencia'>
                Como referencia, en este período se cobró de media <b>{pesos.format(referencia.porHora)} por hora</b>{' '}
                ({pesos.format(referencia.porReserva)} por reserva), ya con descuentos, en {referencia.conCobro}{' '}
                {referencia.conCobro === 1 ? 'reserva' : 'reservas'} con cobro.
                {referencia.cerradasEnCero > 0 &&
                  ` Ojo: otras ${referencia.cerradasEnCero} terminaron sin cobro ($0).`}
              </Text>
            )}
          </Box>

          <Box overflowX='auto'>
            <Table size='sm' variant='unstyled'>
              <Thead>
                <Tr borderBottomWidth='1px' borderColor='gray.200'>
                  <Th pl={2} color='gray.500' fontSize='11px'>
                    Día
                  </Th>
                  <Th color='gray.500' fontSize='11px'>
                    Hora
                  </Th>
                  <Th color='gray.500' fontSize='11px'>
                    Sala
                  </Th>
                  <Th color='gray.500' fontSize='11px'>
                    Cliente
                  </Th>
                  <Th color='gray.500' fontSize='11px'>
                    Estado
                  </Th>
                  <Th color='gray.500' fontSize='11px' pr={2}>
                    Valor estimado (COP)
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {reservas.map((r) => {
                  const estado = ESTADOS[r.estado] ?? { nombre: r.estado, color: 'gray' };
                  return (
                    <Tr key={r.id} borderBottomWidth='1px' borderColor='gray.100' data-testid='vienen-fila'>
                      <Td pl={2} py={2} whiteSpace='nowrap'>
                        {format(r.dia, "EEE d 'de' MMM", { locale: esLocale }).replace('.', '')}
                      </Td>
                      <Td py={2} whiteSpace='nowrap'>
                        <Text>
                          {horaCorta(r.inicio)} – {horaCorta(r.fin)}
                        </Text>
                        <Text fontSize='xs' color='gray.500'>
                          {horas(r.fin - r.inicio)}
                        </Text>
                      </Td>
                      <Td py={2}>{nombreSala[r.locationId] ?? '—'}</Td>
                      <Td py={2} maxW='200px'>
                        <Text noOfLines={1}>{r.nombre || '—'}</Text>
                        <Text fontSize='xs' color='gray.500'>
                          {r.code}
                        </Text>
                      </Td>
                      <Td py={2}>
                        <Badge colorScheme={estado.color} fontSize='0.65em'>
                          {estado.nombre}
                        </Badge>
                      </Td>
                      <Td py={2} pr={2}>
                        <InputPesos
                          size='sm'
                          maxW='150px'
                          value={borrador[r.id] ?? ''}
                          onChange={(cifras) => setBorrador((b) => ({ ...b, [r.id]: cifras }))}
                          placeholder='0'
                          aria-label={`Valor estimado de la reserva ${r.code}, en pesos`}
                          data-testid='vienen-valor'
                        />
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>
        <ModalFooter gap={2} flexWrap='wrap'>
          <Box flex='1' minW='220px'>
            <Text fontSize='sm' data-testid='vienen-total'>
              <b>{pesos.format(total)}</b> estimado en {conValor} de {reservas.length}{' '}
              {reservas.length === 1 ? 'reserva' : 'reservas'} · {horas(minutosTotales)}
            </Text>
            <Text fontSize='xs' color='gray.500'>
              Solo para esta simulación: no se guarda nada ni cambia ninguna reserva.
            </Text>
          </Box>
          <Button variant='ghost' size='sm' onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size='sm'
            colorScheme='orange'
            onClick={() => onUsar(valores)}
            isDisabled={total === 0 && !iniciales}
            data-testid='usar-vienen'
          >
            Usar en la simulación
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SimuladorMeta;
